import { Autocomplete, TextField, TextFieldVariants } from "@mui/material";
import { useQuery } from "react-query";
import { useIntl } from "react-intl";
import { useState, useRef } from "react";
import useDebounce from "hooks/useDebounce";
import useGlobalContext from "hooks/globalData";
import {
  AdminListRequestOfProductFilterV2,
  Product,
  ProductFilterV2,
} from "services/KnowfullyAdminClient";

type ProductOption = {
  id: string;
  label: string;
};

type SelectProductMultipleProps = {
  value: string[];
  onChange: (v: string[]) => void;
  variant?: TextFieldVariants;
  disabled?: boolean;
  organizationId?: string;
};

const SelectProductMultiple = ({
  onChange,
  value,
  variant,
  disabled,
  organizationId,
}: SelectProductMultipleProps) => {
  const { productService } = useGlobalContext();
  const { formatMessage: t } = useIntl();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm.trim(), 500);
  const labelCache = useRef(new Map<string, string>());

  const cacheProducts = (products?: Product[]) => {
    products?.forEach((p) => {
      if (p.id) labelCache.current.set(p.id, p.computedName ?? p.name ?? p.id);
    });
  };

  const searchRequest = AdminListRequestOfProductFilterV2.fromJS({
    organizationId,
    pageNumber: 0,
    pageSize: 20,
    sortField: "computedName",
    parameters: ProductFilterV2.fromJS({
      searchText: debouncedSearch || undefined,
    }),
  })

  const { data: searchData, isLoading: isSearching } = useQuery(
    ["productService.getProductsV2", searchRequest],
    () =>
      productService.getProductsV2(searchRequest),
    {
      keepPreviousData: true,
      onSuccess: (res) => cacheProducts(res?.data),
    }
  );

  const unresolvedIds = value.filter((id) => !labelCache.current.has(id));

  const resolveReqeust = AdminListRequestOfProductFilterV2.fromJS({
    organizationId,
    pageNumber: 0,
    pageSize: unresolvedIds.length,
    parameters: ProductFilterV2.fromJS({
      includedIds: unresolvedIds,
    }),
  })

  const { data: resolveData, isLoading: isResolving } = useQuery(
    ["productService.getProductsV2", resolveReqeust],
    () =>
      productService.getProductsV2(resolveReqeust),
    {
      enabled: unresolvedIds.length > 0,
      keepPreviousData: true,
      onSuccess: (res) => cacheProducts(res?.data),
    }
  );

  cacheProducts(searchData?.data);
  cacheProducts(resolveData?.data);

  const getLabel = (id: string) => labelCache.current.get(id) ?? id;

  const selectedOptions: ProductOption[] = value.map((id) => ({
    id,
    label: getLabel(id),
  }));

  const options = (() => {
    const seen = new Set(value);
    const result = [...selectedOptions];

    searchData?.data?.forEach((p) => {
      if (p.id && !seen.has(p.id)) {
        result.push({ id: p.id, label: p.computedName ?? p.name ?? p.id });
        seen.add(p.id);
      }
    });

    return result;
  })();

  return (
    <Autocomplete<ProductOption, true>
      multiple
      options={options}
      value={selectedOptions}
      loading={isSearching || isResolving}
      disabled={disabled}
      filterSelectedOptions
      inputValue={searchTerm}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      ChipProps={{ size: "small" }}
      onInputChange={(_, inputValue, reason) => {
        if (reason !== "reset") setSearchTerm(inputValue);
      }}
      onChange={(_, newValue) => {
        onChange(newValue.map((o) => o.id));
        setSearchTerm("");
      }}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          variant={variant}
          label={t({ id: "productLabel" })}
        />
      )}
    />
  );
};

export default SelectProductMultiple;
